import React, { useState, useEffect } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { RootState } from './store';
import { setSelectedProject, Project, loadWorkflows, createProject } from './store/project/projectSlice';
import { signOut } from './store/authSlice';
import store from './store';
import AppInitializer from './components/AppInitializer';
import SessionExpiredDialog from './components/auth/SessionExpiredDialog';
import MaintenanceBanner from './components/common/MaintenanceBanner';
import SessionWatch from './components/auth/SessionWatch';
import { fixResizeObserverErrors } from './utils/ResizeObserverErrorFix';
import ModernLayout from './components/layout/ModernLayout';
import ToolClassPanel from './components/workflow/panels/ToolboxPanel/ToolClassPanel';
import ToolInstancePanel from './components/workflow/panels/ToolboxPanel/ToolInstancePanel';
import WorkflowCanvas from './components/workflow/canvas/WorkflowCanvas';
import CanvasToolbar from './components/workflow/canvas/CanvasToolbar';
import { HELP_ASK_EVENT } from './help/helpBus';
import AppFooter from './components/layout/AppFooter';
import RightSidePanel from './components/layout/RightSidePanel';
import ExecutionScreen from './components/execution/ExecutionScreen';
import { ReactFlowProvider } from '@xyflow/react';
import { fetchConnections } from './store/connectionsSlice';
import { fetchDataDefinitions } from './store/dataDefinitionsSlice';
import { AppDispatch } from './store';
import AppHeader, { Screen } from './components/layout/AppHeader';
import TooltipLayer from './components/common/TooltipLayer';
import { AppDialogHost } from './components/common/AppDialog';
import { ToastHost } from './components/common/Toast';
import AppErrorBoundary from './components/common/AppErrorBoundary';
import DashboardScreen from './components/dashboard/DashboardScreen';
import { installGlobalErrorHandlers } from './services/eventLog';
import { useTranslation } from 'react-i18next';
import TimeZoneSync from './components/common/TimeZoneSync';
import PointerOverlay from './components/common/PointerOverlay';
import { setKnownDefinitions } from './components/workflow/utils/definitionRefs';
import { hydrateLinkedNodes } from './store/workflow/workflowSlice';

// 복원된 Designer 컴포넌트
const Designer: React.FC = () => (
  <ReactFlowProvider>
    <ModernLayout
      children={{
        toolbox: (
          <div className="w-[160px] flex-shrink-0 h-full min-h-0 overflow-hidden border-r border-gray-200 bg-white">
            <ToolClassPanel />
          </div>
        ),
        canvas: (
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            <ToolInstancePanel />
            <div className="flex-1 relative min-w-0">
              {/* 다운로드 버튼은 캔버스 영역 안에 띄운다 — 상단 DFD 검사 바·의존성 바 위에 겹치지 않도록 */}
              <WorkflowCanvas overlay={<CanvasToolbar />} />
            </div>
          </div>
        ),
        // 노드 속성 + 데이터 목록을 한 영역의 탭으로 (이전: 300px + 260px 두 컬럼) — 너비는 400px 로 확대
        // 위치(오른쪽/아래)·크기·접힘은 ModernLayout 의 PropertyDock 이 정한다
        property: <RightSidePanel />,
      }}
    />
  </ReactFlowProvider>
);

const ProjectSelectionScreen: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const allProjects = useSelector((state: RootState) => state.project.projects);
  // 내가 만든 프로젝트만 — 공유받은 것은 들어간 뒤 상단 프로젝트 이름을 눌러 연다(AppHeader).
  // 관계를 모르는 예전 저장 경로면 전부. 내 것이 하나도 없으면(공유만 받은 사람) 들어갈 수 있게 공유받은 것도 보인다
  const owned = allProjects.filter((p) => !p.relation || p.relation === 'owner');
  const projects = owned.length > 0 ? owned : allProjects;
  const sharedCount = allProjects.length - owned.length;
  const { loading, saving, error } = useSelector((state: RootState) => state.project);
  const user = useSelector((state: RootState) => state.auth.user);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !user) return;
    const r = await dispatch(createProject({ name: name.trim(), description: description.trim() || undefined, ownerUsrid: user.usrid }));
    if (createProject.fulfilled.match(r)) {
      setCreating(false); setName(''); setDescription('');
      dispatch(setSelectedProject(r.payload.id));
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">{t('layout:project.select')}</h1>
          <div className="text-xs text-gray-500">
            {user?.name || user?.loginId}
            <button className="ml-2 underline" onClick={() => dispatch(signOut())}>{t('layout:project.logout')}</button>
          </div>
        </div>
        {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        {loading && <div className="mb-4 text-sm text-gray-500">{t('common:ui.loading')}</div>}
        {!loading && projects.length === 0 && !creating && (
          <div className="mb-4 text-sm text-gray-500">{t('layout:project.none')}</div>
        )}
        <div className="space-y-4">
          {projects.map((project: Project) => (
            <button
              key={project.id}
              className="w-full py-3 px-4 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors text-left"
              onClick={() => dispatch(setSelectedProject(project.id))}
            >
              <div className="flex items-baseline gap-2">
                <div className="font-medium text-lg flex-1 min-w-0 truncate">{project.name}</div>
                {/* 남의 프로젝트는 소유자와 내 역할을 보여 준다 — 같은 이름(샘플 복제본 등)을 구분하게 (docs/PROJECT_ACCESS.md) */}
                {project.relation && project.relation !== 'owner' && (
                  <span className="shrink-0 text-xs text-gray-500">
                    {t('layout:project.sharedBy', { owner: project.ownerUsrid || '-', role: t(`layout:members.role.${project.relation}`) })}
                  </span>
                )}
              </div>
              {project.description && (
                <div className="text-sm text-gray-500 mt-1">{project.description}</div>
              )}
            </button>
          ))}
        </div>
        {owned.length > 0 && sharedCount > 0 && (
          <div className="mt-3 text-xs text-gray-500">{t('layout:project.sharedHint', { count: sharedCount })}</div>
        )}
        <div className="mt-6 border-t pt-4">
          {creating ? (
            <form onSubmit={submitCreate} className="space-y-3">
              <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder={t('layout:project.namePh')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              <input value={description} onChange={e => setDescription(e.target.value)} placeholder={t('layout:project.descPh')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setCreating(false)} className="px-3 py-1.5 text-sm border rounded">{t('common:ui.cancel')}</button>
                <button type="submit" disabled={saving || !name.trim()} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50">
                  {saving ? t('layout:project.saving') : t('layout:project.create')}
                </button>
              </div>
            </form>
          ) : (
            <button onClick={() => setCreating(true)} className="w-full py-2 text-sm text-blue-700 border border-dashed border-blue-300 rounded hover:bg-blue-50">
              {t('layout:project.new')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const MainApp: React.FC = () => {
  const [screen, setScreen] = useState<Screen>('dashboard');
  // 도움말 「AI 어시스턴트에 물어보기」 — AI 에이전트 창은 디자이너에만 있으므로 화면부터 옮긴다 (help/helpBus)
  useEffect(() => {
    const onAsk = () => setScreen('designer');
    window.addEventListener(HELP_ASK_EVENT, onAsk);
    return () => window.removeEventListener(HELP_ASK_EVENT, onAsk);
  }, []);
  // 대시보드에서 '실행' 을 누른 워크플로 — 실행 탭이 열릴 때 그것을 골라 둔다
  const [runnerWorkflowId, setRunnerWorkflowId] = useState<string | null>(null);
  const selectedProjectId = useSelector((state: RootState) => state.project.selectedProjectId);
  const dispatch = useDispatch<AppDispatch>();

  // ResizeObserver 패치 적용
  useEffect(() => {
    fixResizeObserverErrors();
  }, []);

  // 전역 오류 후크 — 렌더 밖(이벤트 처리기·Promise)에서 난 예외를 기록으로 남긴다.
  // ResizeObserver 패치 뒤에 붙여야 그쪽이 억제하는 경고를 우리가 다시 주워 담지 않는다.
  useEffect(() => {
    installGlobalErrorHandlers();   // 앱 기동 기록(app.start)도 그 안에서 한 번만 남긴다
  }, []);

  // 프로젝트 선택 시 그 프로젝트의 워크플로 목록을 QBase 에서 읽는다
  useEffect(() => {
    if (selectedProjectId) {
      dispatch(loadWorkflows(selectedProjectId));
    }
  }, [selectedProjectId, dispatch]);

  // App 마운트 시 connection 목록 fetch 및 콘솔 출력
  useEffect(() => {
    dispatch(fetchConnections()).then((action) => {
      if ('payload' in action) {
        console.log('Fetched connections (App mount):', action.payload);
      } else {
        console.log('Failed to fetch connections (App mount):', action);
      }
    });
  }, [dispatch]);

  // App 마운트 시 데이터 정의 목록 fetch
  useEffect(() => {
    dispatch(fetchDataDefinitions());
  }, [dispatch]);
  // 데이터 노드는 정의 id 만 저장된다 — 정의 목록을 받을 때마다(정의를 고친 뒤 포함) 열린 캔버스의 값을 다시 채운다
  const definitions = useSelector((s: RootState) => s.dataDefinitions.items);
  useEffect(() => {
    setKnownDefinitions(definitions);
    dispatch(hydrateLinkedNodes());
  }, [definitions, dispatch]);

  if (!selectedProjectId) {
    return <ProjectSelectionScreen />;
  }

  return (
      // 화면 전체를 flex 컬럼으로: 탭 바는 고정, 본문은 남은 높이(flex-1 + min-h-0)를 정확히 차지한다.
      // 이전의 h-[calc(100vh-40px)] 는 탭 바 실제 높이와 어긋나면 하단이 넘치거나 남았다.
      <div className="w-full h-screen flex flex-col overflow-hidden">
        {/* 시간대(시스템 기본 + 프로젝트 재정의)를 날짜 표시·DAG 변환에 맞춘다 — 화면 없음 */}
        <TimeZoneSync />
        {/* 도움말·Quick Start·AI 에이전트가 화면을 가리키는 손가락 (help/pointer) — 화면 없음 */}
        <PointerOverlay />
        <AppHeader screen={screen} onChangeScreen={setScreen} />
        <div className="flex-1 min-h-0 overflow-hidden">
          {screen === 'dashboard' && (
            <DashboardScreen
              onOpenRunner={(id) => { setRunnerWorkflowId(id); setScreen('runner'); }}
              onOpenDesigner={() => setScreen('designer')}
            />
          )}
          {screen === 'runner' && <ExecutionScreen initialWorkflowId={runnerWorkflowId} />}
          {screen === 'designer' && <Designer />}
        </div>
        <AppFooter />
      </div>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      {/* 렌더 중 예외를 붙잡는다 — 없으면 흰 화면만 남고 기록도 없다 */}
      <AppErrorBoundary>
        <AppInitializer>
          <MainApp />
        </AppInitializer>
        <TooltipLayer />
        <AppDialogHost />
        <ToastHost />
        {/* 열어 둔 채 세션이 끊기면 지금 화면 위에서 다시 로그인한다 — 캔버스를 잃지 않게 (services/sessionExpiry) */}
        <SessionExpiredDialog />
        {/* 세션이 살아 있는지 주기적으로 확인 — QBase 목록은 로그인이 풀려도 401 이 아니라 빈 목록을 준다 */}
        <SessionWatch />
        {/* 큐베이스 점검(쓰기 동결·DB 전환) 중임을 알리는 띠 — 로그인 화면에서도 보이게 맨 바깥에 (services/maintenance) */}
        <MaintenanceBanner />
      </AppErrorBoundary>
    </Provider>
  );
};

export default App; 